/* eslint-disable no-restricted-syntax */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  createContext,
  useCallback,
  useState,
  useContext,
  useEffect,
} from 'react';

import api from '../services/api';
import {
  trackAddToCart as ga4AddToCart,
  trackBeginCheckout as ga4BeginCheckout,
  trackPurchase as ga4Purchase,
  IGA4Item,
} from '../utils/ga4';

interface IMarketing {
  id: number;
  type: string;
  code: string;
  add_cart: boolean;
  start_payment: boolean;
  checkout: boolean;
}

export interface IItem {
  id: string;
  name: string;
  quantity: number;
  price: number;
}

interface MarketingContextData {
  setEventId: React.Dispatch<React.SetStateAction<number>>;
  trackAddToCart: (items: IItem[]) => void;
  trackStartPayment: (items: IItem[]) => void;
  trackCheckout: (items: IItem[], transactionId?: string) => void;
}

export const MarketingContext = createContext<MarketingContextData>(
  {} as MarketingContextData
);

export const MarketingProvider: React.FC = ({ children }) => {
  const [eventId, setEventId] = useState(0);
  const [addCart, setAddCart] = useState(false);
  const [startPayment, setStartPayment] = useState(false);
  const [checkout, setCheckout] = useState(false);

  // GA4 dinâmico do organizador
  const [gaAddCart, setGaAddCart] = useState(false);
  const [gaStartPayment, setGaStartPayment] = useState(false);
  const [gaCheckout, setGaCheckout] = useState(false);
  const [gaCodes, setGaCodes] = useState<string[]>([]);

  const loadCodes = useCallback(async () => {
    if (eventId) {
      api.get<IMarketing[]>(`/marketing/events/${eventId}`).then((response) => {
        const dynamicGaCodes: string[] = [];
        for (const marketing of response.data) {
          if (marketing.type === 'google_analytics') {
            // Armazena flags GA4 do organizador
            setGaAddCart(marketing.add_cart);
            setGaStartPayment(marketing.start_payment);
            setGaCheckout(marketing.checkout);
            dynamicGaCodes.push(marketing.code);

            const existingScript = document.querySelector(
              `script[data-dynamic-ga="${marketing.code}"]`
            );
            if (existingScript) {
              existingScript.remove();
            }

            // Remove scripts dinâmicos antigos
            const oldScripts = document.querySelectorAll(
              'script[data-dynamic-ga]'
            );
            oldScripts.forEach((s) => s.remove());

            const script = document.createElement('script');
            script.setAttribute('data-dynamic-ga', marketing.code);
            script.async = true;
            script.src = `https://www.googletagmanager.com/gtag/js?id=${marketing.code}`;
            document.head.appendChild(script);

            const inlineScript = document.createElement('script');
            inlineScript.setAttribute('data-dynamic-ga', marketing.code);
            inlineScript.innerHTML = `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${marketing.code}');
          `;
            document.head.appendChild(inlineScript);
          }

          if (marketing.type === 'facebook') {
            setAddCart(marketing.add_cart);
            setStartPayment(marketing.start_payment);
            setCheckout(marketing.checkout);
            const existingScripts = document.querySelectorAll(
              'script[data-dynamic-fb]'
            );
            existingScripts.forEach((s) => s.remove());

            // Cria script externo do fbevents.js
            const externalScript = document.createElement('script');
            externalScript.async = true;
            externalScript.src =
              'https://connect.facebook.net/en_US/fbevents.js';
            externalScript.setAttribute('data-dynamic-fb', 'true');
            document.head.appendChild(externalScript);

            // Cria o script inline com o ID do pixel
            const inlineScript = document.createElement('script');
            inlineScript.setAttribute('data-dynamic-fb', 'true');
            inlineScript.innerHTML = `
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');

            fbq('init', '${marketing.code}');
            fbq('track', 'PageView');
          `;
            document.head.appendChild(inlineScript);
          }
        }
        setGaCodes(dynamicGaCodes);
      });
    }
  }, [eventId]);

  useEffect(() => {
    loadCodes();
  }, [loadCodes]);

  const trackAddToCart = useCallback(
    (items: IItem[]) => {
      // Facebook Pixel
      if (window.fbq && addCart) {
        const content_ids = items.map((item) => item.id);
        const contents = items.map((item) => ({
          id: item.id,
          name: item.name,
          quantity: item.quantity,
          item_price: item.price,
        }));
        window.fbq('track', 'AddToCart', {
          content_ids,
          contents,
          content_type: 'product',
          value: items
            .reduce((acc, item) => acc + item.price * item.quantity, 0)
            .toFixed(2),
          currency: 'BRL',
        });
      }

      // GA4 DataLayer (global + dinâmico do organizador)
      if (gaAddCart || !gaCodes.length) {
        const ga4Items: IGA4Item[] = items.map((item) => ({
          item_id: item.id,
          item_name: item.name,
          price: item.price,
          quantity: item.quantity,
        }));
        const totalValue = items.reduce(
          (acc, item) => acc + item.price * item.quantity,
          0
        );
        ga4AddToCart(ga4Items, totalValue);

        // Envia também para tags GA4 dinâmicas do organizador
        if (window.gtag && gaCodes.length > 0) {
          gaCodes.forEach((code) => {
            if (window.gtag) {
              window.gtag('event', 'add_to_cart', {
                send_to: code,
                currency: 'BRL',
                value: totalValue,
                items: ga4Items,
              });
            }
          });
        }
      }
    },
    [addCart, gaAddCart, gaCodes]
  );

  const trackStartPayment = useCallback(
    (items: IItem[]) => {
      // Facebook Pixel
      if (window.fbq && startPayment) {
        const content_ids = items.map((item) => item.id);
        const contents = items.map((item) => ({
          id: item.id,
          name: item.name,
          quantity: item.quantity,
          item_price: item.price,
        }));
        window.fbq('track', 'InitiateCheckout', {
          content_ids,
          contents,
          content_type: 'product',
          value: items
            .reduce((acc, item) => acc + item.price * item.quantity, 0)
            .toFixed(2),
          currency: 'BRL',
        });
      }

      // GA4 DataLayer - begin_checkout (global + dinâmico)
      if (gaStartPayment || !gaCodes.length) {
        const ga4Items: IGA4Item[] = items.map((item) => ({
          item_id: item.id,
          item_name: item.name,
          price: item.price,
          quantity: item.quantity,
        }));
        const totalValue = items.reduce(
          (acc, item) => acc + item.price * item.quantity,
          0
        );
        ga4BeginCheckout(ga4Items, totalValue);

        // Envia também para tags GA4 dinâmicas do organizador
        if (window.gtag && gaCodes.length > 0) {
          gaCodes.forEach((code) => {
            if (window.gtag) {
              window.gtag('event', 'begin_checkout', {
                send_to: code,
                currency: 'BRL',
                value: totalValue,
                items: ga4Items,
              });
            }
          });
        }
      }
    },
    [startPayment, gaStartPayment, gaCodes]
  );

  const trackCheckout = useCallback(
    (items: IItem[], transactionId?: string) => {
      // Facebook Pixel
      if (window.fbq && checkout) {
        const content_ids = items.map((item) => item.id);
        const contents = items.map((item) => ({
          id: item.id,
          name: item.name,
          quantity: item.quantity,
          item_price: item.price,
        }));
        window.fbq('track', 'Purchase', {
          content_ids,
          contents,
          content_type: 'product',
          value: items
            .reduce((acc, item) => acc + item.price * item.quantity, 0)
            .toFixed(2),
          currency: 'BRL',
        });
      }

      // GA4 DataLayer - purchase (client-side global + dinâmico)
      if (gaCheckout || !gaCodes.length) {
        const ga4Items: IGA4Item[] = items.map((item) => ({
          item_id: item.id,
          item_name: item.name,
          price: item.price,
          quantity: item.quantity,
        }));
        const totalValue = items.reduce(
          (acc, item) => acc + item.price * item.quantity,
          0
        );
        const txId =
          transactionId ||
          `KORO_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        // Envia para o dataLayer global (GTM da Koro)
        ga4Purchase(txId, ga4Items, totalValue);

        // Envia também para tags GA4 dinâmicas do organizador
        if (window.gtag && gaCodes.length > 0) {
          gaCodes.forEach((code) => {
            if (window.gtag) {
              window.gtag('event', 'purchase', {
                send_to: code,
                transaction_id: txId,
                currency: 'BRL',
                value: totalValue,
                items: ga4Items,
              });
            }
          });
        }
      }
    },
    [checkout, gaCheckout, gaCodes]
  );

  return (
    <MarketingContext.Provider
      value={{ setEventId, trackAddToCart, trackStartPayment, trackCheckout }}
    >
      {children}
    </MarketingContext.Provider>
  );
};

export function useMarketing(): MarketingContextData {
  const context = useContext(MarketingContext);

  if (!context) {
    throw new Error('useMarketing must be used within an MarketingProvider');
  }

  return context;
}
